import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersistence from 'vuex-persist';
import { APIService } from '@/resources/campanha.js';
import {
  isFuture, isPast, endOfDay, addHours,
} from 'date-fns';
import LogRocket from 'logrocket';
import createPlugin from 'logrocket-vuex';
import axios from 'axios';
import { everything } from '@sejazipper/layer-api-client';
import { useZipperApi } from '@/composables/useZipperApi';
import { useVuexSellerStores } from '@/composables/useVuexSellerStores';

const { client } = useZipperApi();
const logrocketPlugin = createPlugin(LogRocket);
const api = new APIService();

Vue.use(Vuex);

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: (state) => ({
    catalogue: state.catalogue, lastAgendaCheck: state.lastAgendaCheck, count: state.count, best: state.best, primeiraCompra: state.primeiraCompra, risco: state.risco, ativos: state.ativos, inativos: state.inativos, perdidos: state.perdidos, lead: state.lead, store: state.store, customer: state.customer,
  }),
});

const compareObjects = (obj1, obj2) => {
  if (!obj1 && !obj2) return true;

  if ((obj1 && !obj2) || (!obj1 && obj2)) return false;
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);
  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const key of obj1Keys) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }
  return true;
};

export const store = new Vuex.Store({
  plugins: [logrocketPlugin, vuexLocal.plugin],
  state: {
    customers: [],
    hasMultitenant: false,
    catalogueSortAsc: false,
    catalogueFilters: [],
    catalogueSectorFilters: 'Todos os produtos',
    profile: null,
    agenda: [],
    count: [],
    best: [],
    primeiraCompra: [],
    risco: [],
    ativos: [],
    inativos: [],
    perdidos: [],
    lead: [],
    catalogue: [],
    products: [],
    selectedProductColor: null,
    store: null,
    customer: null,
    agendaMarca: [],
    tasks: [],
    selectedTask: null,
    selectedUser: null,
    lastAgendaCheck: null,
    packageVersion: JSON.parse(unescape(process.env.PACKAGE_JSON || '%7Bversion%3A0%7D')).version,
    isNewAccount: false,
    maxFavorites: false,
    orders: {},
    catalogueId: null,
    person: { cpf: null, type: null },
    personIsIdentified: false,
    checkoutLink: null,
    pendingApprovalRequests: [],
  },
  mutations: {
    SET_CUSTOMERS(state, customers) {
      state.customers = customers;
    },
    HAS_MULTITENANT(state, hasMultitenant) {
      state.hasMultitenant = hasMultitenant;
    },
    REMOVE_CHECKOUT(state, product) {
      state.catalogue = state.catalogue.filter((item) => item !== product);
    },
    CLEAR_CHECKOUT(state) {
      state.catalogue = [];
    },
    SET_PERFIL(state, seller) {
      state.profile = seller;
    },
    SET_CUSTOMER(state, customer) {
      state.customer = customer;
    },
    SET_STORE(state, store) {
      state.store = store;
    },
    SET_TAREFAS(state, tasks) {
      state.tasks = tasks;
    },
    SET_AGENDA(state, agenda) {
      state.agenda = agenda;
    },
    SET_COUNT(state, agenda) {
      state.count = agenda;
    },
    SET_AGENDA_BEST(state, agenda) {
      state.best = agenda;
    },
    SET_AGENDA_PRIMEIRA(state, agenda) {
      state.primeiraCompra = agenda;
    },
    SET_AGENDA_RISCO(state, agenda) {
      state.risco = agenda;
    },
    SET_AGENDA_ATIVOS(state, agenda) {
      state.ativos = agenda;
    },
    SET_AGENDA_INATIVOS(state, agenda) {
      state.inativos = agenda;
    },
    SET_AGENDA_PERDIDOS(state, agenda) {
      state.perdidos = agenda;
    },
    SET_AGENDA_LEAD(state, agenda) {
      state.lead = agenda;
    },
    SET_AGENDAMARCA(state, agendaMarca) {
      state.agendaMarca = agendaMarca;
    },
    ADD_AGENDA(state, agenda) {
      state.agenda = state.agenda.concat(agenda);
    },
    ADD_AGENDA_BEST(state, agenda) {
      state.best = state.best.concat(agenda);
    },
    ADD_AGENDA_PRIMEIRA(state, agenda) {
      state.primeiraCompra = state.primeiraCompra.concat(agenda);
    },
    ADD_AGENDA_RISCO(state, agenda) {
      state.risco = state.risco.concat(agenda);
    },
    ADD_AGENDA_ATIVOS(state, agenda) {
      state.ativos = state.ativos.concat(agenda);
    },
    ADD_AGENDA_INATIVOS(state, agenda) {
      state.inativos = state.inativos.concat(agenda);
    },
    ADD_AGENDA_PERDIDOS(state, agenda) {
      state.perdidos = state.perdidos.concat(agenda);
    },
    ADD_AGENDA_LEAD(state, agenda) {
      state.lead = state.lead.concat(agenda);
    },
    SET_SELECTEDTASK(state, task) {
      state.selectedTask = task;
    },
    SET_SELECTEDUSER(state, user) {
      state.selectedUser = user;
    },
    SET_SELECTED_PRODUCT_COLOR(state, color) {
      state.selectedProductColor = color;
    },
    ADD_TO_CATALOGUE(state, product) {
      state.catalogue.push(product);
    },
    REFRESH_CATALOGUE(state) {
      const products = [];

      // eslint-disable-next-line array-callback-return
      state.catalogue.forEach((item) => {
        const existingProduct = products.find(
          (product) => product.id === item.id
            && compareObjects(product.warranty, item.warranty)
            && compareObjects(product.warrantyMobile, item.warrantyMobile)
            && !product.services.length && !item.services.length
            && product.imei === item.imei,
        );

        if (existingProduct) {
          existingProduct.quantity += item.quantity;
        } else {
          products.push(item);
        }
      });

      // eslint-disable-next-line no-param-reassign
      state.catalogue = products;
    },
    ADD_PRODUCTS_TO_CATALOGUE(state, products) {
      state.catalogue = state.catalogue.concat(products);
    },
    SET_PRODUCTS(state, products) {
      state.products = products;
    },
    SET_CATALOGUE_SORT_ASC(state, catalogueSortAsc) {
      state.catalogueSortAsc = catalogueSortAsc;
    },
    SET_CATALOGUE_FILTERS(state, filters) {
      state.catalogueFilters = filters;
    },
    SET_CATALOGUE_SECTOR_FILTERS(state, sectorFilters) {
      state.catalogueSectorFilters = sectorFilters;
    },
    LAST_AGENDA_CHECK(state, date) {
      state.lastAgendaCheck = date;
    },
    SET_NEW_ACCOUNT(state, isNewAccount) {
      state.isNewAccount = isNewAccount;
    },
    SET_MAX_FAVORITE_CATALOGUES(state, isMax) {
      state.maxFavorites = isMax;
    },
    SET_ORDERS(state, orders) {
      state.orders = orders;
    },
    SET_CATALOGUE_ID(state, catalogueId) {
      state.catalogueId = catalogueId;
    },
    SET_PERSON(state, person) {
      state.person = person;
    },
    SET_CHECKOUT_LINK(state, checkoutLink) {
      state.checkoutLink = checkoutLink;
    },
    SET_APPROVAL_REQUESTS(state, approvalRequests) {
      state.pendingApprovalRequests = approvalRequests;
    },
    SET_PERSON_AS_IDENTIFIED(state, value) {
      state.personIsIdentified = value;
    },
  },
  actions: {
    carregarPerfil({ commit }, { user, customer }) {
      const callback = (response) => {
        const store = this.state.store ? this.state.store : response.stores.find((store) => store.sellers.find((s) => s.id == user.sub));
        const seller = response.stores
          .map((s) => s.sellers)
          .reduce((a, b) => a.concat(b))
          .find((s) => s.id == user.sub);
        commit('SET_CUSTOMER', response);
        commit('SET_PERFIL', seller);
        commit('SET_STORE', store);

        axios.defaults.headers.common['Kon-AuthId'] = this.state.profile.id;
        axios.defaults.headers.common['Kon-Seller'] = this.state.profile.name.replace(/\W/g, ' ');
        axios.defaults.headers.common['Kon-Store'] = this.state.customer.name;
        axios.defaults.headers.common['Kon-Version'] = this.state.packageVersion;

        api.getTaskByCustomerIdAndSeller(response.id, seller.name).then(async (data) => {
          const tasks = data;
          commit('SET_TAREFAS', tasks);
        });
      };

      if (customer) { callback(customer); } else { api.getCustomer(user.sub).then(callback); }
    },
    carregarContatoTask({ commit }) {
      api.getContact(this.state.selectedTask.person.id).then(async (data) => {
        const user = data;
        commit('SET_SELECTEDUSER', user);
      });
    },

    carregarContatoTask({ commit }) {
      api.getContact(this.state.selectedTask.person.id).then(async (data) => {
        const user = data;
        commit('SET_SELECTEDUSER', user);
      });
    },
    clearAgenda({ commit }, situacao) {
      const situacoes = ['BEST', 'PRIMEIRA', 'RISCO', 'ATIVOS', 'INATIVOS', 'PERDIDOS', 'LEAD'];
      if (!situacao) {
        situacoes.map((s) => commit(`SET_AGENDA_${s}`, []));
      } else {
        commit(`SET_AGENDA_${situacao}`);
      }
    },
    carregarListaContatos({ commit, dispatch }, { user, force }, situacao) {
      dispatch('clearAgenda', situacao);
    },
    carregarAgendaMarca({ commit }, user) {
      api.getCustomer(user.sub).then((response) => {
        api.getAllResumedBysellerId(response.id, user.sub).then((data) => {
          const agendaMarca = data;
          commit('SET_AGENDAMARCA', agendaMarca);
        });
      });
    },
    setProducts({ commit }, products) {
      commit('SET_PRODUCTS', products);
    },
    setSelectedProductColor({ commit }, color) {
      commit('SET_SELECTED_PRODUCT_COLOR', color);
    },
    addToCatalogue({ commit }, product) {
      commit('ADD_TO_CATALOGUE', product);
    },
    refreshCatalogue({ commit }) {
      commit('REFRESH_CATALOGUE');
    },
    addProductsToCatalogue({ commit }, products) {
      const productsToAdd = products.map((product) => (
        this.state.catalogue.find((item) => (item.id === product.id))
        || product
      ));

      commit('ADD_PRODUCTS_TO_CATALOGUE', productsToAdd);
    },
    removeCheckout({ commit }, product) {
      commit('REMOVE_CHECKOUT', product);
    },
    clearCheckout({ commit }) {
      commit('CLEAR_CHECKOUT');
    },
    setMaxFavoriteCatalogues({ commit }, isMax) {
      commit('SET_MAX_FAVORITE_CATALOGUES', isMax);
    },
    setOrders({ commit }, orders) {
      commit('SET_ORDERS', orders);
    },
    setCatalogueId({ commit }, catalogueId) {
      commit('SET_CATALOGUE_ID', catalogueId);
    },
    setPerson({ commit }, person) {
      commit('SET_PERSON', person);
    },
    setPersonAsIdentified({ commit }, value) {
      commit('SET_PERSON_AS_IDENTIFIED', value);
    },
    setCheckoutLink({ commit }, checkoutLink) {
      commit('SET_CHECKOUT_LINK', checkoutLink);
    },
    getPendingApprovalRequests({ commit }, root) {
      try {
        if (!this.getters.isManager && !this.getters.isDirector) {
          commit('SET_APPROVAL_REQUESTS', []);
          return;
        }

        const { stores } = useVuexSellerStores(root);
        const storesIds = stores.value.map((s) => s.id);

        client.chain.query
          .getPendingApprovalRequestsByStoresIds({
            customerId: this.state.customer.id,
            storesIds,
          })
          .get({ ...everything, product: { ...everything } })
          .then((res) => {
            commit('SET_APPROVAL_REQUESTS', res);
            this.dispatch('getPendingApprovalRequests', root);
          });
      } catch (e) {
        console.log(e);
      }
    },
    setCatalogueSectorFilters({ commit }, sectorFilter) {
      commit('SET_CATALOGUE_SECTOR_FILTERS', sectorFilter);
    },
  },
  getters: {
    getMelhoresClientes: (state) => state.best,
    getPrimeiraCompra: (state) => state.primeiraCompra,
    getLeads: (state) => state.lead,
    getClientesAtivos: (state) => state.ativos,
    getClientesRisco: (state) => state.risco,
    getClientesInativos: (state) => state.inativos,
    getClientesPerdidos: (state) => state.perdidos,
    getCount: (state) => state.count,
    getClientes: (state) => state.agenda.concat(state.best, state.primeiraCompra, state.ativos, state.inativos, state.risco, state.perdidos, state.lead),
    getTarefasAtrasadas: (state) => state.tasks.filter((task) => isPast(endOfDay(addHours(task.due, 3))) && !task.end),
    getTarefasEmDia: (state) => state.tasks.filter((task) => isFuture(endOfDay(addHours(task.due, 3))) && !task.end),
    getTarefasConcluidas: (state) => state.tasks.filter((task) => task.end),
    isManager: (state) => state.profile && !!state.profile.manager,
    isDirector: (state) => state.profile && !!state.profile.director,
    isSeller: (state) => state.profile && !!state.profile.seller,
    isMaxFavoriteCatalogues: (state) => state.maxFavorites,
    containsCatalogue: (state) => state.customer && (state.customer.useCatalogue || state.store.useCatalogue),
    appVersion: (state) => state.packageVersion,
    getStore: (state) => state.store,
    getCustomer: (state) => state.customer,
    getProfile: (state) => state.profile,
    getCatalogue: (state) => state.catalogue,
    getCatalogueSortAsc: (state) => state.catalogueSortAsc,
    getCatalogueSectorFilters: (state) => state.catalogueSectorFilters,
    getOrders: (state) => state.orders,
    getCatalogueId: (state) => state.catalogueId,
    getProducts: (state) => state.products,
    getSelectedProductColor: (state) => state.selectedProductColor,
    getPerson: (state) => state.person,
    getCheckoutLink: (state) => state.checkoutLink,
    getPendingApprovalRequests: (state) => state.pendingApprovalRequests,
    personIsIdentified: (state) => state.personIsIdentified,
  },
});
